/**
 * The API client, and the session it carries.
 *
 * Tokens live in localStorage. That is a deliberate, bounded choice: an access
 * token lasts fifteen minutes and a refresh token is revoked the moment it is
 * reused, so the exposure from an XSS is a short window rather than a standing
 * credential — and the alternative, a cookie, would need CSRF handling on an
 * activation endpoint, which is a worse thing to get wrong.
 *
 * A 401 triggers exactly one refresh attempt. More than one and a genuinely
 * dead session becomes a loop hammering the API during an incident, which is
 * the moment it can least afford it.
 */

const BASE = import.meta.env.VITE_API_URL ?? 'https://api.staging.evac.duress.com';

const ACCESS = 'evac.access';
const REFRESH = 'evac.refresh';

export const session = {
  get access(): string | null {
    return localStorage.getItem(ACCESS);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS, access);
    localStorage.setItem(REFRESH, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS);
    localStorage.removeItem(REFRESH);
  },
  get signedIn(): boolean {
    return Boolean(localStorage.getItem(ACCESS));
  },
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * Anything the server sent alongside the message.
     *
     * A refusal that names what is in the way can be turned into a
     * destination — "this site already has a live event" carries the id of the
     * event that is running — and a screen cannot offer that if the client
     * throws the sentence away and keeps nothing else.
     */
    readonly detail: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

/**
 * The message a mutation's error carries, or null if there is none. An
 * `ApiError` carries the server's own sentence — which is written to be shown
 * to an operator — so it is preferred; anything else falls back to its message.
 */
export function errorMessage(error: unknown): string | null {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return null;
}

/**
 * The query-level retry policy. A 4xx is the server's considered answer —
 * a 401 will not become a 200 by asking again, and retrying it turns one
 * expired session into hundreds of requests hammering the API mid-incident.
 * Anything else (network, 5xx) gets one retry.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 1;
}

/**
 * Subscribers told when the session dies for good — a refresh the server
 * refused, or a 401 that no refresh can cure. The app routes to sign-in on
 * this signal *without touching the URL*, so the address bar still holds the
 * live event view and a fresh sign-in lands the operator straight back on it.
 */
const sessionExpiredListeners = new Set<() => void>();

export function onSessionExpired(listener: () => void): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

function expireSession(): void {
  const hadSession = session.signedIn || Boolean(session.refresh);
  session.clear();
  // Only announce a session that actually existed — an unauthenticated 401
  // (someone poking a URL signed out) is not an expiry.
  if (hadSession) for (const listener of sessionExpiredListeners) listener();
}

/**
 * The one in-flight refresh. Refresh tokens rotate with reuse detection, so
 * two concurrent refreshes are self-destruction: the first rotates the token,
 * the second presents the now-revoked one, and the server — unable to tell a
 * race from a theft — revokes every session the user holds. When a screen's
 * worth of queries 401 at once (a restarted API, an expired access token),
 * they must all await a single refresh, not race their own.
 */
let refreshInFlight: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  const token = session.refresh;
  if (!token) return false;

  let res: Response;
  try {
    res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken: token }),
    });
  } catch {
    // Network failure — the API restarting, a dropped connection. The token
    // may be perfectly good, so keep the session and let a later attempt
    // succeed rather than signing the operator out over a blip.
    return false;
  }

  if (res.status >= 500) {
    // The server is unwell, not the token. Same reasoning as the network case.
    return false;
  }

  if (!res.ok) {
    // A definitive refusal — revoked, expired, reused. The session is dead
    // and staying signed "in" just produces an endless wall of 401s that an
    // operator mid-incident cannot tell from an outage.
    expireSession();
    return false;
  }

  const body = (await res.json()) as { accessToken: string; refreshToken: string };
  session.set(body.accessToken, body.refreshToken);
  return true;
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
  retried = false,
): Promise<T | null> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  if (init.body) headers['content-type'] = 'application/json';
  const access = session.access;
  if (access) headers.authorization = `Bearer ${access}`;

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  if (res.status === 401 && access) {
    if (!retried && session.refresh) {
      if (await refreshSession()) return api<T>(path, init, true);
      // Refresh did not produce a session. If that was a definitive refusal
      // the session is already expired (and announced); a transient failure
      // keeps the session for the next attempt, and this call just errors.
    } else if (retried || !session.refresh) {
      // A 401 no refresh can cure: either the freshly-refreshed access token
      // was still refused, or there is no refresh token to try. Sign out
      // rather than leaving the operator on a spinner that looks like an
      // outage.
      expireSession();
    }
  }

  if (res.status === 204) return null;

  if (!res.ok) {
    let message = res.statusText;
    let detail: Record<string, unknown> = {};
    try {
      const body = (await res.json()) as { error?: string } & Record<string, unknown>;
      if (body.error) message = body.error;
      // Everything except the message, which is already carried above.
      detail = Object.fromEntries(Object.entries(body).filter(([k]) => k !== 'error'));
    } catch {
      // A non-JSON error body is still an error; the status carries the meaning.
    }
    throw new ApiError(res.status, message, detail);
  }

  return (await res.json()) as T;
}

// ---- shapes the API returns -------------------------------------------------

/**
 * The five roles docs/permissions.md ratifies. Owner is a strict superset
 * of Admin for writes; Viewer reads at the admin level and writes nothing.
 */
export type Role = 'owner' | 'admin' | 'viewer' | 'staff' | 'occupant';

export interface Membership {
  org_id: string;
  role: Role;
  org_name: string;
  org_slug: string;
  /** The org's country — how a national-form mobile is read (migration 0138). */
  org_country?: string;
  /**
   * The work mobile this org holds for the person (migration 0138): the number
   * other administrators call from the authorise sheet. `null` when there is
   * none; `verified` once they typed back the code texted to it.
   */
  work_mobile?: WorkMobile | null;
  /** Whether this membership owes a work mobile (owner, admin or staff — any country). */
  work_mobile_required?: boolean;
  /**
   * The WORK profile this organisation sees (migration 0166) — and only this
   * organisation. Absent from an older server.
   */
  work?: WorkProfile;
}

/**
 * What one organisation knows the person as (docs/account-modes.md). `name`
 * is what they are called there: the one they chose (`nameChosen`), else the
 * directory's (`directoryName`), else null (the org then shows `email`).
 * `photoUrl` null means the organisation sees no photo at all — never the
 * personal one.
 */
export interface WorkProfile {
  name: string | null;
  nameChosen: string | null;
  directoryName: string | null;
  email: string | null;
  photoUrl: string | null;
}

/** The PERSONAL profile: family, contacts and strangers see it; no organisation does. */
export interface PersonalProfile {
  name: string | null;
  email: string | null;
  photoUrl: string | null;
  mobile: string | null;
  mobileVerified: boolean;
  /** False for a work-only account: a stranger in a help ask sees "Someone nearby". */
  shownToStrangers: boolean;
}

export interface WorkMobile {
  phone: string;
  /** Formatted for reading: "+61 412 345 678". */
  display: string;
  verified: boolean;
}

/**
 * The mandatory work-mobile step (migration 0138), decided by the server.
 * `promptNow` is false while anything is running in one of the person's orgs,
 * so the prompt never stands between them and an alert; `verifyAvailable` is
 * false where no code can be sent, and the step then lets them carry on.
 */
export interface WorkMobileStatus {
  required: boolean;
  promptNow: boolean;
  verifyAvailable: boolean;
}

/** True for Owner and Admin — the roles that can operate admin surfaces. */
export const isAdminRole = (r: Role): boolean => r === 'owner' || r === 'admin';

/** True only for Owner — the seat that can transfer ownership and change billing. */
export const isOwnerRole = (r: Role): boolean => r === 'owner';

/** True for Viewer — hides every write CTA. */
export const isViewerRole = (r: Role): boolean => r === 'viewer';

export interface Me {
  userId: string;
  /**
   * Whether the current access token was minted within
   * `ADMIN_FRESHNESS_SECONDS` — the mutation-freshness gate. Falls to false
   * on its own after that window; a fresh sign-in or an OTP re-verify
   * restores it. Client uses this to open the re-verify dialog before
   * calling a `requireFresh`-guarded route rather than after the 403.
   */
  fresh: boolean;
  /** The person's own display name, may be null if never set. */
  displayName: string | null;
  /**
   * The email the sign-in code goes to. Required by the OTP re-verify
   * dialog so the person does not have to retype it during an incident.
   */
  email: string | null;
  memberships: Membership[];
  /** Absent from an older server — read as "nothing required". */
  workMobile?: WorkMobileStatus;
  /** The personal side (0166). Absent from an older server. */
  personal?: PersonalProfile;
}

/** The work name this org sees; null clears the person's choice (0166). */
export const setWorkName = (orgId: string, name: string | null) =>
  api<null>(`/orgs/${orgId}/me/work-profile`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });

/** The work photo this org sees — raw image bytes, base64. */
export const setWorkPhoto = (orgId: string, imageBase64: string, mime: string) =>
  api<{ photoUrl: string }>(`/orgs/${orgId}/me/work-photo`, {
    method: 'POST',
    body: JSON.stringify({ imageBase64, mime }),
  });

/** No work photo: the organisation then sees none. */
export const removeWorkPhoto = (orgId: string) =>
  api<null>(`/orgs/${orgId}/me/work-photo`, { method: 'DELETE' });

/**
 * "Add a work account" with Microsoft (0166): a one-time link that starts the
 * Entra sign-in bound to THIS account. The browser is sent to `url`.
 */
export const startEntraLink = () =>
  api<{ url: string }>('/auth/oidc/link-intent', { method: 'POST', body: JSON.stringify({}) });

/** A corner of a drawn area (0123 assembly areas, 0140 building outlines). */
export interface LatLon {
  latitude: number;
  longitude: number;
}

export interface Site {
  id: string;
  name: string;
  slug: string;
  address: string | null;
  /**
   * The site's sub-premise within the building (migration 0110): unit/suite and
   * floor/level, e.g. "Suite 1" on "Level 8". Captured separately from the
   * street `address` because autocomplete drops the sub-premise. `null` for a
   * whole-building site.
   */
  unit: string | null;
  level: string | null;
  /**
   * The building's own public contact number (migration 0107) — the front desk
   * or security switchboard, shown on the Locations detail summary. `null` when
   * the org has not stated one. Distinct from a person's contact points and
   * from the discovered POI number the voice-bot dials.
   */
  phone: string | null;
  latitude: number | null;
  longitude: number | null;
  sensitive: boolean;
  /**
   * Whether this site counts presence (time-bounded check-in) or falls back to
   * the roster as the alert audience (migration 0081, `docs/roster-and-presence.md`).
   * A tenant on a shared floor typically leaves this off — they have no front
   * desk and nobody to sign visitors in — while a whole building or a campus
   * turns it on. Defaults to `true` server-side (the column is NOT NULL DEFAULT
   * true), so it is treated as on unless it is explicitly `false`.
   *
   * Optional here on purpose: the field arrives with migration 0081 on the
   * setup-wizard-api branch, and while this client is ahead of that merge a
   * `getSites` response simply omits it. Absent therefore reads as the server
   * default (counted), never as an error.
   */
  presence_enabled?: boolean;
  /**
   * The building boundary radius in metres (migration 0111), sized on the setup
   * map. Drawn and stored for future use — nothing on the server or the phone
   * tests anyone against it today. `null` until an admin sets one.
   */
  geofence_radius_m?: number | null;
  /**
   * The building's drawn outline instead of the circle (migration 0140):
   * unclosed corners, 3..64. At most one of this and `geofence_radius_m`.
   * Like the circle, drawn and stored for future use — no containment runs
   * against it today.
   */
  boundary_polygon?: LatLon[] | null;
  /**
   * How many admins a real alert here needs (migration 0134). Optional so an
   * older API that omits it reads as the server default, `one_person`.
   */
  activation_rule?: ActivationRule;
  /** When an administrator last chose the rule; null = never asked (migration 0134). */
  activation_rule_chosen_at?: string | null;
  /**
   * Who asked to let one administrator send a real alert here, while that
   * request waits for a second administrator to confirm; null otherwise.
   */
  activation_rule_loosen_requested_by?: string | null;
  /** Whether any team is linked to this building (migration 0113). */
  has_linked_teams?: boolean;
  followers: number;
}

/**
 * The per-building activation rule (migration 0134, docs/security.md §1.1).
 * `one_person`: one admin may alert staff on site, wider needs two.
 * `two_person`: every real alert here needs a second admin.
 */
export type ActivationRule = 'one_person' | 'two_person';

export type PersonStatus =
  'needs_help' | 'safe_arrived' | 'safe' | 'not_on_site' | 'arrived' | 'not_reached' | 'no_answer';

export interface EventSummary {
  event: {
    id: string;
    mode: 'drill' | 'live';
    state: string;
    site_id: string;
    cap_event: string;
    cap_instruction: string;
    cap_area_desc: string;
    /**
     * The live procedure — the latest transition if the event ever changed
     * (migration 0076). What the operator is running right now, and what the
     * "Change procedure" control offers to move away from.
     */
    procedure: 'evacuation' | 'invacuation' | 'lockdown' | 'communication';
    audience_basis: 'presence' | 'roster' | null;
    activated_at: string | null;
    all_clear_at: string | null;
    closed_at: string | null;
    /**
     * The one-tap preview-and-retract safety deadline (migration 0064).
     *
     * Non-null on events raised via the tier-2 single-admin path; NULL
     * otherwise. After it passes retract remains available as an ordinary
     * correction, but the R3-as-primary-control window is over — signed
     * reports read `retracted_within_window` to distinguish the two.
     */
    retract_deadline_at: string | null;
    retracted_within_window: boolean | null;
    /** When the event was marked sent in error, if it was. */
    corrected_at?: string | null;
    /** The building's name, served beside the row. */
    site_name?: string;
  };
  accountability: {
    notified: number;
    safe: number;
    needsHelp: number;
    unaccounted: number;
    /**
     * The arrival moment (migration 0075). Safe is not clear: `arrived` reached
     * the assembly point, `safeAndClear` both answered safe and got there, and
     * `safeNotArrived` answered safe but has not — the disagreement that keeps
     * "all clear" honest.
     */
    arrived: number;
    safeAndClear: number;
    safeNotArrived: number;
    /**
     * Answered "I'm not here" to the on-site question and never confirmed
     * (migration 0114). Excluded from `unaccounted` and named apart — the
     * unreachable pattern: they were never in the building.
     */
    notOnSite: number;
  };
  /** Follower subscribers told — beside the count, never inside it. */
  followersTold: number;
  /**
   * The org's own thanks, ready to post (docs/gamification.md §Sharing).
   * Server-composed so no screen invents its own wording; null unless the
   * event is closed, non-sensitive, non-silent and not a correction.
   */
  orgShare: { text: string; url: string } | null;
  /** One ISO timestamp per person who has answered safe, ascending. */
  confirmationTimes: string[];
  needsHelpQueue: {
    user_id: string;
    label: string;
    created_at: string;
    note: string | null;
    shared_latitude: number | null;
    shared_longitude: number | null;
  }[];
  /**
   * Four numbers per channel, never collapsed into one. `attempted` is what we
   * tried, `accepted` is what a provider took, `delivered` is what a device
   * acknowledged, `confirmed` is what a human answered — and only the last is
   * evidence of anything (R10).
   */
  channels: {
    channel: string;
    attempted: number;
    accepted: number;
    delivered: number;
    confirmed: number;
  }[];
  /** People no channel reached. Not the same set as people who did not answer. */
  unreachable: { user_id: string; label: string }[];
  /**
   * Everybody who was told, by name, with ONE status each, decided by the
   * server: needs_help · safe_arrived (at `arrived_at`) · safe · not_on_site
   * · arrived (marked at a point, no answer) · not_reached · no_answer. The
   * last three are exactly the rows inside `accountability.unaccounted`.
   * Absent on an older API.
   */
  people?: {
    user_id: string;
    label: string;
    status: PersonStatus;
    note: string | null;
    answered_at: string | null;
    arrived_at: string | null;
  }[];
  /**
   * People being alerted who have a live PEEP at this site: what the plan
   * says, who assists, where they go. The standing register is admin-only;
   * this is the slice a warden is entitled to, at the moment it matters.
   */
  assistance: {
    user_id: string;
    label: string;
    assistance_required: string;
    assistant: string | null;
    assembly_point: string | null;
    confirmed_safe: boolean;
  }[];
  /**
   * How the procedure got to where it is (migration 0076), oldest first. The
   * live procedure rides on `event.procedure`; this is the ordered history
   * behind it. Empty for the overwhelming majority of events, which never
   * change procedure. The command view renders it as a short timeline so an
   * operator can see "this started as an evacuation and became a lockdown".
   */
  transitions: {
    seq: number;
    from: string;
    to: string;
    silent: boolean;
    instruction: string;
    at: string;
    supersededSafe: number;
    delivered: boolean;
    recipients: number;
    by: string | null;
  }[];
  /**
   * True when the server answered with the RECIPIENT's trimmed view
   * (2026-09-22): the event's words and state only, with `accountability`
   * zeroed as a wire-compat artifact. The dashboard is an operator surface —
   * it must never render those zeros as counts (G20); the event screen
   * refuses this view with a sentence instead. Absent on the full view.
   */
  receiverView?: boolean;
  /**
   * The authorisation picture for a live event that waited for a second admin
   * (R1) — pending, rejected or lapsed; null otherwise. `raisedBy.phone` is
   * present only for an admin who could authorise, only while pending.
   */
  /**
   * Where this event sends people and which points were closed for it — what a
   * second admin must see before authorising, and what the live view reads as
   * its ground. Absent on an older API.
   */
  destination?: {
    point: { id: string; name: string } | null;
    closedPoints: { id: string; name: string; redirectTo: string | null }[];
  };
  authorisation?: {
    status: 'pending' | 'rejected' | 'lapsed';
    raisedBy: { label: string; you: boolean; phone: string | null };
    raisedAt: string;
    expiresAt: string;
    windowSeconds: number;
    reason: 'building_two_person' | 'wide_reach';
    audienceCount: number | null;
    canAuthorise: boolean;
    secondsLeft: number;
    stoppedBy: { label: string; you: boolean; withdrawn: boolean } | null;
    stoppedAt: string | null;
  } | null;
}

export const requestCode = (email: string) =>
  api<{ status: string }>('/auth/request-code', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

export async function verifyCode(email: string, code: string): Promise<void> {
  const body = await api<{ accessToken: string; refreshToken: string }>('/auth/verify-code', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
  if (body) session.set(body.accessToken, body.refreshToken);
}

/**
 * Whether SSO sign-in is configured on this deployment. The sign-in screen
 * renders the Microsoft button only when this says so — a button for a door
 * that does not exist is the G20 class.
 */
export const ssoStatus = () => api<{ enabled: boolean }>('/auth/sso');

/**
 * The API-origin door the SSO button opens: a top-level navigation, not a
 * fetch, because the flow is a redirect dance through the IdP. `returnPath`
 * is where the callback sends the person back to afterwards.
 */
export const ssoStartUrl = (returnPath: string) =>
  `${BASE}/auth/oidc/start?return=${encodeURIComponent(returnPath)}`;

/**
 * Exchange the single-use handoff code the OIDC callback put in the URL for
 * the same access/refresh pair verify-code mints.
 */
export async function redeemSso(code: string): Promise<void> {
  const body = await api<{ accessToken: string; refreshToken: string }>('/auth/oidc/redeem', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  if (body) session.set(body.accessToken, body.refreshToken);
}

export interface EventListItem {
  id: string;
  mode: 'drill' | 'live';
  state: string;
  procedure: string;
  cap_event: string;
  created_at: string;
  activated_at: string | null;
  closed_at: string | null;
  site_name: string;
  notified: number;
  safe: number;
  has_report: boolean;
}

export const listEvents = (orgId: string) =>
  api<{ events: EventListItem[] }>(`/orgs/${orgId}/events`);

export const updateSite = (
  orgId: string,
  siteId: string,
  body: {
    /** The building's own name. Blank is refused (name is the point of the row). */
    name?: string;
    /** The street address. An empty string clears it back to none. */
    address?: string;
    /** Sub-premise (migration 0110): "Suite 1" / "Level 8". Empty string clears. */
    unit?: string;
    level?: string;
    /** Building boundary radius in metres (migration 0111). null clears it. */
    geofenceRadiusM?: number | null;
    /** A drawn outline instead (migration 0140); null clears. Setting either clears the other. */
    boundaryPolygon?: LatLon[] | null;
    /**
     * The building's public contact number (migration 0107). An empty string
     * clears it back to none; the server trims and rejects an over-long value.
     */
    phone?: string | null;
    latitude?: number;
    longitude?: number;
    sensitive?: boolean;
    /** Migration 0081 — turn presence counting on or off for this site. */
    presenceEnabled?: boolean;
    /**
     * Migration 0134 — one or two admins for a real alert here. Loosening to
     * one_person answers 202 `{ pendingLoosen: true }` until a DIFFERENT
     * administrator sends the same change.
     */
    activationRule?: ActivationRule;
  },
) =>
  api<{ pendingLoosen?: boolean; message?: string } | null>(`/orgs/${orgId}/sites/${siteId}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/**
 * Remove a never-evented building. A building with recorded events is refused
 * by the server (migration 0018 — its compliance history IS the record), and
 * the 409's words are what the screen shows.
 */
export const deleteSite = (orgId: string, siteId: string) =>
  api<null>(`/orgs/${orgId}/sites/${siteId}`, { method: 'DELETE' });

/* ------------------------------------------ per-building procedure instructions */

/**
 * One building's instruction per procedure type (migration 0093). The read
 * always returns all four procedures — `instruction: null` where the building
 * has written none, so the caller can fall back to the built-in default
 * (`src/lib/procedures.ts`).
 */
export interface SiteProcedure {
  procedure: import('./lib/procedures.ts').Procedure;
  instruction: string | null;
}

export const siteProcedures = (orgId: string, siteId: string) =>
  api<{ procedures: SiteProcedure[] }>(`/orgs/${orgId}/sites/${siteId}/procedures`);

/**
 * Set (or clear) a building's instruction for one procedure. A blank
 * instruction clears the stored row on the server, reverting that procedure to
 * the built-in default.
 */
export const setSiteProcedure = (
  orgId: string,
  siteId: string,
  procedure: import('./lib/procedures.ts').Procedure,
  instruction: string,
) =>
  api<null>(`/orgs/${orgId}/sites/${siteId}/procedures/${procedure}`, {
    method: 'PUT',
    body: JSON.stringify({ instruction }),
  });

export interface Diagram {
  id: string;
  placement: string;
  last_reviewed: string;
  overdue: boolean;
  due_soon: boolean;
}

export const getDiagrams = (orgId: string, siteId: string) =>
  api<{ diagrams: Diagram[]; readiness: { diagrams: number; overdue: number; due_soon: number } }>(
    `/orgs/${orgId}/sites/${siteId}/diagrams`,
  );

export const recordDiagram = (
  orgId: string,
  siteId: string,
  placement: string,
  lastReviewed: string,
) =>
  api<{ diagramId: string }>(`/orgs/${orgId}/sites/${siteId}/diagrams`, {
    method: 'POST',
    body: JSON.stringify({ placement, lastReviewed }),
  });

/* ------------------------------------------------ site equipment register */

/**
 * The fixed, site-held equipment a building has bolted to a wall (migrations
 * 0069/0072, docs/emergency-equipment.md Tier 1). Not a person-linked record —
 * a placement on a wall, never a coordinate (R4).
 */
export type EquipmentClass =
  | 'aed'
  | 'first_aid_kit'
  | 'adrenaline_autoinjector'
  | 'reliever_inhaler'
  | 'oxygen'
  | 'evac_chair'
  | 'spill_kit'
  | 'fire_blanket'
  | 'other';

export type EquipmentAccess =
  | 'unrestricted'
  | 'locked_cabinet'
  | 'staffed_hours_only'
  | 'behind_reception'
  | 'requires_training';

export interface Equipment {
  id: string;
  class: EquipmentClass;
  description: string;
  placement_note: string | null;
  zone_id: string | null;
  zone_name: string | null;
  access_constraint: EquipmentAccess;
  available_hours: string | null;
  expiry_date: string | null;
  last_checked_at: string | null;
  last_checked_note: string | null;
  source: 'manually_added' | 'imported_from_diagram' | 'imported_from_registry';
  external_registry: string | null;
  external_registry_id: string | null;
  notes: string | null;
  /** Computed state — the honesty signals (E9). Never hidden, never dressed up. */
  unverified: boolean;
  expired: boolean;
  expiring_soon: boolean;
  overdue_service: boolean;
}

export interface EquipmentReadiness {
  total: number;
  unverified: number;
  expired: number;
  expiring_soon: number;
  overdue_service: number;
  aed_count: number;
  autoinjector_count: number;
  reliever_count: number;
}

/** The editable fields — everything except computed state and the join names. */
export interface EquipmentInput {
  class: EquipmentClass;
  description: string;
  placementNote?: string | null;
  zoneId?: string | null;
  accessConstraint?: EquipmentAccess;
  availableHours?: string | null;
  expiryDate?: string | null;
  lastCheckedAt?: string | null;
  lastCheckedNote?: string | null;
  notes?: string | null;
}

export const getEquipment = (orgId: string, siteId: string) =>
  api<{ equipment: Equipment[]; readiness: EquipmentReadiness }>(
    `/orgs/${orgId}/sites/${siteId}/equipment`,
  );

export const createEquipment = (orgId: string, siteId: string, body: EquipmentInput) =>
  api<{ equipmentId: string }>(`/orgs/${orgId}/sites/${siteId}/equipment`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateEquipment = (
  orgId: string,
  siteId: string,
  equipmentId: string,
  body: EquipmentInput,
) =>
  api<null>(`/orgs/${orgId}/sites/${siteId}/equipment/${equipmentId}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const removeEquipment = (orgId: string, siteId: string, equipmentId: string) =>
  api<null>(`/orgs/${orgId}/sites/${siteId}/equipment/${equipmentId}/remove`, { method: 'POST' });

export interface AuditEntry {
  id: number;
  action: string;
  detail: Record<string, unknown> | null;
  created_at: string;
  actor: string | null;
}

export const getAudit = (orgId: string) => api<{ entries: AuditEntry[] }>(`/orgs/${orgId}/audit`);

export const renameZone = (orgId: string, siteId: string, zoneId: string, name: string) =>
  api<null>(`/orgs/${orgId}/sites/${siteId}/zones/${zoneId}/rename`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

/**
 * A short-lived LiveKit join token for one event's stream room.
 *
 * Answers 501 (as an ApiError) when `STREAM_PROVIDER=none` — the caller
 * reads that as "there is no stream tile", never as a failure. The token is
 * subscriber-only; publishing is a separate ask the server grants only to
 * staff and above.
 */
export interface StreamToken {
  url: string;
  room: string;
  token: string;
  expiresAt: number;
}

export const getStreamToken = (orgId: string, eventId: string) =>
  api<StreamToken>(`/orgs/${orgId}/events/${eventId}/stream-token`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const getMe = () => api<Me>('/auth/me');
export const getSites = (orgId: string) => api<{ sites: Site[] }>(`/orgs/${orgId}/sites`);
export const getEvent = (orgId: string, eventId: string) =>
  api<EventSummary>(`/orgs/${orgId}/events/${eventId}`);

/**
 * Optional extras beyond the name+address minimum. Coordinates place the site
 * on the live view map; `sensitive` refuses the public follow door (the schema
 * enforces both — a sensitive site's follow link 404s regardless of who asks);
 * `presenceEnabled` (migration 0081) lets the setup wizard turn presence on for
 * a whole building and off for a tenant on a shared floor at creation time.
 */
export interface CreateSiteExtras {
  /** The sub-premise (migration 0110): "Suite 1", "Level 8". */
  unit?: string;
  level?: string;
  /** The building boundary radius in metres (migration 0111). */
  geofenceRadiusM?: number;
  /** …or a drawn outline (migration 0140) — never both. */
  boundaryPolygon?: LatLon[];
  latitude?: number;
  longitude?: number;
  sensitive?: boolean;
  presenceEnabled?: boolean;
  /** The audited override of a duplicate match ("It's a different building"). */
  confirmDistinct?: boolean;
  /** Migration 0134 — the wizard always sends an explicit choice. */
  activationRule?: ActivationRule;
}

export const createSite = (
  orgId: string,
  name: string,
  address?: string,
  extras: CreateSiteExtras = {},
) =>
  api<{ siteId: string }>(`/orgs/${orgId}/sites`, {
    method: 'POST',
    body: JSON.stringify({
      name,
      address,
      // Only send the keys the caller actually set — the server rejects a bare
      // NaN or an unpaired coordinate, and `undefined` is dropped by JSON
      // serialisation while an explicit `null` would be a validation error.
      ...(extras.unit ? { unit: extras.unit } : {}),
      ...(extras.level ? { level: extras.level } : {}),
      ...(extras.geofenceRadiusM !== undefined ? { geofenceRadiusM: extras.geofenceRadiusM } : {}),
      ...(extras.boundaryPolygon ? { boundaryPolygon: extras.boundaryPolygon } : {}),
      ...(extras.latitude !== undefined ? { latitude: extras.latitude } : {}),
      ...(extras.longitude !== undefined ? { longitude: extras.longitude } : {}),
      ...(extras.sensitive ? { sensitive: true } : {}),
      ...(extras.presenceEnabled !== undefined ? { presenceEnabled: extras.presenceEnabled } : {}),
      ...(extras.confirmDistinct ? { confirmDistinct: true } : {}),
      ...(extras.activationRule ? { activationRule: extras.activationRule } : {}),
    }),
  });

/**
 * Is this place already one of the org's buildings? The same rule the create
 * and update routes enforce with a 409 `site_duplicate` (same address after
 * normalisation, or a pin within 25 m, same unit/level), asked early so the
 * wizard can say so before anything is filled in.
 */
export interface SiteMatch {
  siteId: string;
  name: string;
  reason: 'address' | 'nearby';
  message: string;
}

export const matchSite = (
  orgId: string,
  q: {
    address?: string;
    unit?: string;
    level?: string;
    latitude?: number;
    longitude?: number;
    excludeSiteId?: string;
  },
) => {
  const params = new URLSearchParams();
  if (q.address) params.set('address', q.address);
  if (q.unit) params.set('unit', q.unit);
  if (q.level) params.set('level', q.level);
  if (q.latitude !== undefined && q.longitude !== undefined) {
    params.set('latitude', String(q.latitude));
    params.set('longitude', String(q.longitude));
  }
  if (q.excludeSiteId) params.set('excludeSiteId', q.excludeSiteId);
  return api<{ match: SiteMatch | null }>(`/orgs/${orgId}/sites/match?${params.toString()}`);
};

/**
 * Create an organisation. Anyone signed in may do this — trust-score v1 is the
 * manual `verified` flag on the org, and going live is gated elsewhere. The
 * caller becomes its Owner (the one billing seat, per `docs/permissions.md`),
 * so this is the only legitimate producer of an initial Owner row.
 */
export const createOrg = (
  name: string,
  countryCode: string,
  workMobile?: string,
  workMobileCountryCode?: string,
) =>
  api<{ orgId: string; slug: string; name: string; workMobileVerified: boolean }>(`/orgs`, {
    method: 'POST',
    body: JSON.stringify({
      name,
      countryCode: countryCode.toUpperCase(),
      ...(workMobile ? { workMobile } : {}),
      ...(workMobile && workMobileCountryCode ? { workMobileCountryCode } : {}),
    }),
  });

/* -------------------------------------------------------------- work mobile */

/**
 * Text a code to a mobile (migration 0138). The server normalises what was
 * typed (any country — a national-format number is read in `countryCode`), rate-limits, and answers every refusal with a sentence
 * (`ApiError.message`) plus a `code` in `detail` — `verification_unavailable`
 * means no code can be sent here right now and the person may carry on.
 */
export const startPhoneVerification = (phone: string, countryCode?: string) =>
  api<{ phone: string; display: string; expiresInSeconds: number }>('/me/phone/verify/start', {
    method: 'POST',
    body: JSON.stringify({ phone, ...(countryCode ? { countryCode } : {}) }),
  });

/**
 * Check the code. A right one makes the number the person's VERIFIED work
 * mobile in every org they belong to, or only the ones named.
 */
export const checkPhoneVerification = (phone: string, code: string, orgIds?: string[]) =>
  api<{ verified: boolean; phone: string; display: string; orgIds: string[] }>(
    '/me/phone/verify/check',
    {
      method: 'POST',
      body: JSON.stringify({ phone, code, ...(orgIds ? { orgIds } : {}) }),
    },
  );

/**
 * An administrator adds or corrects a colleague's work mobile (admin + fresh
 * sign-in). Always stored unverified — the person is asked to verify it at
 * their next sign-in.
 */
export const setMemberWorkMobile = (
  orgId: string,
  userId: string,
  phone: string,
  countryCode?: string,
) =>
  api<{ workMobile: WorkMobile }>(`/orgs/${orgId}/people/${userId}/work-mobile`, {
    method: 'POST',
    body: JSON.stringify(countryCode ? { phone, countryCode } : { phone }),
  });

/**
 * Save the person's own work mobile WITHOUT a code — "carry on" when no code
 * could be sent. Stored unverified; the app keeps asking them to confirm it.
 */
export const saveOwnWorkMobile = (phone: string, countryCode?: string, orgIds?: string[]) =>
  api<{ workMobile: WorkMobile; orgIds: string[] }>('/me/work-mobile', {
    method: 'POST',
    body: JSON.stringify({
      phone,
      ...(countryCode ? { countryCode } : {}),
      ...(orgIds ? { orgIds } : {}),
    }),
  });

/** Remove the person's own work mobile (all orgs, or the ones named). */
export const removeOwnWorkMobile = (orgIds?: string[]) =>
  api<null>('/me/work-mobile', {
    method: 'DELETE',
    body: JSON.stringify(orgIds ? { orgIds } : {}),
  });

/* -------------------------------------------------------------- invitations */

export interface InvitationSummary {
  organisation: string;
  role: Role;
  countryCode: string;
  requiresWorkMobile: boolean;
}

/** What an invitation is for — anyone holding the token may read it. */
export const getInvitation = (token: string) =>
  api<InvitationSummary>(`/invitations/${encodeURIComponent(token)}`);

/**
 * Accept (signed in). The work mobile rides along; `workName` is what this org
 * will call them; `linkCode` is the code emailed to the invited address when
 * they accept on a DIFFERENT account (0166 — see `startInvitationLink`).
 */
export const acceptInvitation = (
  token: string,
  workMobile?: string,
  workMobileCountryCode?: string,
  extra: { workName?: string; linkCode?: string } = {},
) =>
  api<{ orgId: string; role: Role; workMobileVerified: boolean | null }>(
    `/invitations/${encodeURIComponent(token)}/accept`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...(workMobile
          ? { workMobile, ...(workMobileCountryCode ? { workMobileCountryCode } : {}) }
          : {}),
        ...(extra.workName ? { workName: extra.workName } : {}),
        ...(extra.linkCode ? { linkCode: extra.linkCode } : {}),
      }),
    },
  );

/** Email a code to the invited address, to accept on the account signed in now. */
export const startInvitationLink = (token: string) =>
  api<{ status: string }>(`/invitations/${encodeURIComponent(token)}/link/start`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

/** Decline in one tap, no sign-in. */
export const declineInvitation = (token: string) =>
  api<null>(`/invitations/${encodeURIComponent(token)}/decline`, { method: 'POST' });

/* -------------------------------------------------------- org join link */

/**
 * The organisation join link (migration 0142): one link per org, posted into a
 * Slack or Teams channel, that joins whoever holds it as an occupant. The
 * public read names the organisation and nothing else; unknown, expired and
 * revoked links all answer one identical 404.
 */
export interface JoinLinkSummary {
  organisation: string;
  role: 'occupant';
}

export const getJoinLink = (token: string) =>
  api<JoinLinkSummary>(`/join/${encodeURIComponent(token)}`);

/** The admin's view of the org's link: whether one is live, never the token. */
export interface JoinLinkStatus {
  active: boolean;
  expiresAt: string | null;
  joinedCount: number;
}

export const getJoinLinkStatus = (orgId: string) => api<JoinLinkStatus>(`/orgs/${orgId}/join-link`);

/** Mint (rotating any live link). The plaintext URL exists only in this answer. */
export const mintJoinLink = (orgId: string) =>
  api<{ url: string; expiresAt: string }>(`/orgs/${orgId}/join-link`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const revokeJoinLink = (orgId: string) =>
  api<{ revoked: boolean }>(`/orgs/${orgId}/join-link/revoke`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

/** Join through the link (signed in). Never changes an existing member's role. */
export const acceptJoinLink = (token: string) =>
  api<{ orgId: string; role: Role; joined: boolean }>(`/join/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

/** The machine code a refusal carries (`{error: sentence, code}`), if any. */
export function errorCode(error: unknown): string | null {
  if (error instanceof ApiError && typeof error.detail.code === 'string') return error.detail.code;
  return null;
}

/**
 * How far an alert reaches, which is what decides whether it needs a second
 * admin (R1 amendment, `docs/security.md` §1.1). Ordered narrowest first, the
 * same order the database enum uses to compare them.
 */
export type AudienceScope = 'site_workforce' | 'site_staff' | 'site_all' | 'cross_org';

export interface RaiseBody {
  mode: 'drill' | 'live';
  instruction: string;
  areaDesc: string;
  procedure?: 'evacuation' | 'invacuation' | 'lockdown' | 'communication';
  audienceScope?: AudienceScope;
  /**
   * Which of the building's points are closed for this alert from the start.
   * The server checks them against the building and refuses to close the last
   * open point of the procedure's kind (`last_open_point`); a closure is
   * recorded as the same `point_closed` update the live view writes,
   * redirecting to the nearest open point. People go to any open point —
   * there is no primary — so `assemblyPointId` (an explicit single
   * destination the server still accepts) is not sent from the dashboard.
   */
  assemblyPointId?: string;
  closedAssemblyPointIds?: string[];
}

/**
 * Raise an event.
 *
 * A drill activates in one step. A live event activates in one step **only**
 * at `site_staff` — the people already in the building, who can check the
 * claim with their own eyes — and otherwise lands in `pending_second_confirm`
 * for a different admin to authorise. The server decides that, not the caller:
 * the state comes back in the response and the screen reads it rather than
 * predicting it.
 */
export const raiseEvent = (orgId: string, siteId: string, body: RaiseBody) =>
  api<{
    eventId: string;
    state: string;
    recipients: number;
    /**
     * True iff this raise engaged the tier-2 preview-and-retract path
     * (migration 0064, `docs/security.md` §1.1 tier 2). When true, the
     * raise dialog stays open until `retractDeadlineAt` passes so the
     * admin never has to leave it to hit Retract.
     */
    previewAndRetract: boolean;
    retractDeadlineAt: string | null;
    /**
     * Who was told this alert is waiting for them (migration 0134): every
     * other current administrator, by name. Empty when it went out at once.
     */
    authorisers?: string[];
  }>(`/orgs/${orgId}/sites/${siteId}/events`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** R1's second pair of eyes. Refused by the database if it is the same person. */
export const authoriseEvent = (orgId: string, eventId: string) =>
  api<{ state: string; recipients: number }>(`/orgs/${orgId}/events/${eventId}/activate`, {
    method: 'POST',
  });

/**
 * The second admin refusing.
 *
 * Only ever reaches an unsent event. Anything that reached people is stood
 * down through the all-clear or retracted as a correction — never cancelled
 * into silence, because people are already walking out of a building.
 */
export const cancelEvent = (orgId: string, eventId: string) =>
  api<{ eventId: string; state: string; outcome: 'rejected' | 'lapsed' | 'already_stopped' }>(
    `/orgs/${orgId}/events/${eventId}/cancel`,
    { method: 'POST' },
  );

export const allClear = (orgId: string, eventId: string) =>
  api<null>(`/orgs/${orgId}/events/${eventId}/all-clear`, { method: 'POST' });

export const closeEvent = (orgId: string, eventId: string) =>
  api<null>(`/orgs/${orgId}/events/${eventId}/close`, { method: 'POST' });

/* -------------------------------------------------------- what a site is for */

export interface PeepReadiness {
  live: number;
  unassigned: number;
  review_overdue: number;
  expiring_soon: number;
  no_step_free_route: number;
}

export interface Peep {
  id: string;
  user_id: string;
  profile: AccessibilityProfile;
  assistance_required: string;
  assisted_by: string | null;
  assembly_point_id: string | null;
  expires_at: string | null;
  review_due: string | null;
  agreed_with_person_at: string;
  person: string | null;
  assistant: string | null;
  assembly_point: string | null;
  live: boolean;
}

/**
 * Admin-only. This is health information, and the one screen in the product
 * that a warden cannot open.
 */
export const getPeeps = (orgId: string, siteId: string) =>
  api<{ peeps: Peep[]; readiness: PeepReadiness }>(`/orgs/${orgId}/sites/${siteId}/peeps`);

export const recordPeep = (
  orgId: string,
  siteId: string,
  body: {
    userId: string;
    profile: AccessibilityProfile;
    assistanceRequired: string;
    assistedBy?: string | null;
    expiresAt?: string | null;
    reviewDue?: string | null;
  },
) =>
  api<{ peepId: string }>(`/orgs/${orgId}/sites/${siteId}/peeps`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const withdrawPeep = (orgId: string, siteId: string, peepId: string) =>
  api<null>(`/orgs/${orgId}/sites/${siteId}/peeps/${peepId}/withdraw`, { method: 'POST' });

export interface Readiness {
  siteName: string;
  zones: number;
  assemblyPoints: number;
  ecoAssignments: number;
  chiefWardenAppointed: boolean;
  lastDrillClosedAt: string | null;
  assembly: {
    kind: AssemblyKind;
    points: number;
    has_default: boolean | null;
    total_capacity: number | null;
    step_free_unassessed: number;
    step_free_none: boolean;
    missing_step_free_instruction: number;
  }[];
  peep: PeepReadiness;
  reach: { audience: number; reachable: number; unreachable: number; basis: string };
  equipment: EquipmentReadiness;
  peopleToPlace: number;
  audienceBasis: 'presence' | 'roster';
  gaps: string[];
  ready: boolean;
}

export const getReadiness = (orgId: string, siteId: string) =>
  api<Readiness>(`/orgs/${orgId}/sites/${siteId}/readiness`);

/**
 * A government agency's warning, attributed to the agency (W1). We are a
 * consumer of these, never an author, and nothing about them can touch the
 * activation path (W3).
 */
export interface PublicWarning {
  id: string;
  source: string;
  headline: string;
  description: string | null;
  instruction: string | null;
  cap_severity: string;
  cap_urgency: string;
  cap_certainty: string;
  /** Normalized type for the glanceable icon (0126). */
  event_kind: string;
  /** The agency's own event string, verbatim. */
  cap_event: string | null;
  area_description: string | null;
  expires_at: string;
  min_latitude: number;
  max_latitude: number;
  min_longitude: number;
  max_longitude: number;
  /**
   * The one relevance rule (server migration 0154): `high` is an Emergency
   * Warning, a Watch and Act, anything Severe that is not a low-value product,
   * or any earthquake / tsunami; `low` is everything else (sheep graziers,
   * frost, flood watches, marine, road closures…). The reads return `high`
   * only unless asked for minor ones. Optional: an older API sends none.
   */
  relevance?: 'high' | 'low';
}

/** One named district a warning covers — its own box, never the union. */
export interface WarningArea {
  name: string;
  min_latitude: number;
  max_latitude: number;
  min_longitude: number;
  max_longitude: number;
}

export interface BrowseWarning extends PublicWarning {
  /** The districts the warning names (geocode-placed warnings). */
  areas: WarningArea[] | null;
  /** Every CAP polygon the document carried ("lat,lon lat,lon …"). */
  cap_area_polygons: string[] | null;
}

export interface WarningMatch extends PublicWarning {
  site_id: string;
  site_name: string;
}

/** The agencies' in-force warnings joined to this org's own buildings. */
export const getWarningMatches = (orgId: string, includeMinor = false) =>
  api<{ matches: WarningMatch[] }>(
    `/orgs/${orgId}/warning-matches${includeMinor ? '?include=minor' : ''}`,
  );

/** Everything in force inside a map viewport — browse, pull not push. */
export const warningsIn = (
  box: {
    minLatitude: number;
    maxLatitude: number;
    minLongitude: number;
    maxLongitude: number;
  },
  includeMinor = false,
) =>
  api<{ warnings: BrowseWarning[] }>(
    `/warnings/in?minLatitude=${box.minLatitude}&maxLatitude=${box.maxLatitude}&minLongitude=${box.minLongitude}&maxLongitude=${box.maxLongitude}${includeMinor ? '&include=minor' : ''}`,
  );

/**
 * The signed-in person's own warning filter (migration 0136). Stored as
 * exclusions, so a kind nobody chose to hide is shown and notified. Personal,
 * not per-org, and it never touches the activation path (W3).
 */
export interface WarningPreferences {
  /** The kinds the server classifies warnings into, in canonical order. */
  kinds: string[];
  /** Kinds not drawn on this person's map. */
  hiddenOnMap: string[];
  /** Kinds this person is not interrupted about (the Home strip today). */
  muted: string[];
}

export const getWarningPreferences = () => api<WarningPreferences>('/me/warning-preferences');

export const putWarningPreferences = (prefs: { hiddenOnMap: string[]; muted: string[] }) =>
  api<WarningPreferences>('/me/warning-preferences', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });

export interface Zone {
  id: string;
  name: string;
  wardens_assigned: number;
  latitude: number | null;
  longitude: number | null;
}

export const getZones = (orgId: string, siteId: string) =>
  api<{ zones: Zone[]; sweepAvailable: boolean }>(`/orgs/${orgId}/sites/${siteId}/zones`);

export const addZone = (orgId: string, siteId: string, name: string) =>
  api<{ zoneId: string }>(`/orgs/${orgId}/sites/${siteId}/zones`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

export const placeZone = (
  orgId: string,
  siteId: string,
  zoneId: string,
  latitude: number | null,
  longitude: number | null,
) =>
  api<null>(`/orgs/${orgId}/sites/${siteId}/zones/${zoneId}/place`, {
    method: 'POST',
    body: JSON.stringify({ latitude, longitude }),
  });

export interface Template {
  cap_event: string;
  kind: 'alert' | 'all_clear' | 'correction';
  locale: string;
  body: string;
}

export const getTemplates = (orgId: string) =>
  api<{ templates: Template[] }>(`/orgs/${orgId}/templates`);

export const addTemplate = (orgId: string, t: Template) =>
  api<null>(`/orgs/${orgId}/templates`, {
    method: 'POST',
    body: JSON.stringify({ capEvent: t.cap_event, kind: t.kind, locale: t.locale, body: t.body }),
  });

export const removeZone = (orgId: string, siteId: string, zoneId: string) =>
  api<null>(`/orgs/${orgId}/sites/${siteId}/zones/${zoneId}/remove`, { method: 'POST' });

export type AssemblyKind = 'outdoor_assembly' | 'indoor_shelter' | 'reunification';

export type AccessibilityProfile =
  'default' | 'step_free' | 'low_vision' | 'low_hearing' | 'cognitive';

export interface AssemblyInstruction {
  profile: AccessibilityProfile;
  language: string;
  instruction: string;
  source: 'authored' | 'template' | 'machine';
  reviewed: boolean;
}

export interface AssemblyPoint {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  kind: AssemblyKind;
  capacity: number | null;
  /** The area radius in metres (migration 0111), drawn as a circle. null = just a pin. */
  radius_m: number | null;
  /** The drawn alternative (migration 0123): 3..64 unclosed corners; mutually
   *  exclusive with radius_m — one point, one area shape. */
  area_polygon: { latitude: number; longitude: number }[] | null;
  /** Tri-state: null means nobody has assessed it, which is not the same as false. */
  step_free_access: boolean | null;
  is_default: boolean;
  instructions: AssemblyInstruction[];
}

export const updateAssemblyPoint = (
  orgId: string,
  siteId: string,
  pointId: string,
  body: {
    /** Rename the point (2026-09-21 — name joined the updatable set). */
    name?: string;
    /** Move the pin — latitude and longitude come as a PAIR or not at all. */
    latitude?: number;
    longitude?: number;
    kind?: AssemblyKind;
    capacity?: number | null;
    /** Area radius in metres (0111); null clears it back to a plain pin. */
    radiusM?: number | null;
    /** The drawn area (0123); null clears it. Setting either shape clears the other. */
    areaPolygon?: { latitude: number; longitude: number }[] | null;
    stepFreeAccess?: boolean | null;
    isDefault?: boolean;
  },
) =>
  api<null>(`/orgs/${orgId}/sites/${siteId}/assembly-points/${pointId}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const setAssemblyInstruction = (
  orgId: string,
  siteId: string,
  pointId: string,
  body: { profile: AccessibilityProfile; language: string; instruction: string },
) =>
  api<{ instructionId: string }>(
    `/orgs/${orgId}/sites/${siteId}/assembly-points/${pointId}/instructions`,
    { method: 'POST', body: JSON.stringify(body) },
  );

export const getAssemblyPoints = (orgId: string, siteId: string) =>
  api<{ assemblyPoints: AssemblyPoint[] }>(`/orgs/${orgId}/sites/${siteId}/assembly-points`);

export const addAssemblyPoint = (
  orgId: string,
  siteId: string,
  name: string,
  latitude: number,
  longitude: number,
  kind: AssemblyKind = 'outdoor_assembly',
  /** Area radius in metres (migration 0111). Omitted = a plain pin, no circle. */
  radiusM?: number,
  /** The drawn area instead of the circle (migration 0123) — one shape, never both. */
  areaPolygon?: LatLon[],
) =>
  api<{ assemblyPointId: string }>(`/orgs/${orgId}/sites/${siteId}/assembly-points`, {
    method: 'POST',
    body: JSON.stringify({
      name,
      latitude,
      longitude,
      kind,
      ...(radiusM !== undefined ? { radiusM } : {}),
      ...(areaPolygon !== undefined ? { areaPolygon } : {}),
    }),
  });

/** Remove an assembly point (`POST .../assembly-points/:pointId/remove`, admin + fresh). */
export const removeAssemblyPoint = (orgId: string, siteId: string, pointId: string) =>
  api<null>(`/orgs/${orgId}/sites/${siteId}/assembly-points/${pointId}/remove`, {
    method: 'POST',
  });

/* ------------------------------------------------------------------- people */

export interface Person {
  user_id: string;
  email: string | null;
  display_name: string | null;
  role: Role;
  deprovisioned_at: string | null;
  /**
   * The calm-state answer to "who is being notified, and how" — the channels
   * this person can actually be reached on if an alert went out now, each flag
   * mirroring the predicate the delivery layer sends on (`GET
   * /orgs/:orgId/people`). `app` is a registered device (they have installed
   * the app); `sms` is a phone with consent and no opt-out for this org;
   * `email` is a usable address. All three false is an honest "no channel" —
   * the person who ends up unaccounted for — and is surfaced, not hidden.
   */
  reach: { app: boolean; sms: boolean; email: boolean };
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  expired: boolean;
  created_at: string;
  /** The team the admin named at invite time — joined at accept (migration 0112). */
  team_name?: string | null;
}

export interface Roster {
  members: Person[];
  pendingInvitations: PendingInvitation[];
  counts: { active: number; admins: number; deprovisioned: number; pending: number };
}

/**
 * Change a member's role. The role picker offers admin, viewer, staff — and
 * `owner` is deliberately absent: Ownership is transferred through a
 * separate endpoint that gates on the current Owner. Making Owner a value
 * in the same select would offer a footgun (two Owners at COMMIT is a
 * database refusal, not a UX story).
 */
export const setRole = (
  orgId: string,
  userId: string,
  role: 'admin' | 'viewer' | 'staff' | 'occupant',
) =>
  api<{ ok: boolean }>(`/orgs/${orgId}/people/${userId}/role`, {
    method: 'POST',
    body: JSON.stringify({ role }),
  });

/**
 * The roster: everyone on it (`members`) and everyone who has been asked and
 * not yet accepted (`pendingInvitations`). The distinction between pending and
 * active is the whole question the People screen and the setup wizard's team
 * step answer — "who is covered, and who is not" — so it is carried in the
 * shape rather than flattened into one list.
 */
export const getRoster = (orgId: string) => api<Roster>(`/orgs/${orgId}/people`);

/**
 * One person on the People list view (`GET /orgs/:orgId/people`, docs/teams.md).
 *
 * `label` is server-computed and is never a bare UUID — a redacted person reads
 * "Redacted" rather than their tombstone address. `teams` is the teams they are
 * in; `sites` is their site associations, read through those teams (teams are
 * the organising axis, so a person's sites are the distinct sites of their
 * teams); `warden` is the ECO/warden roles they hold — a separate axis from both
 * the team and the org role. `onSite` is a boolean only where presence is the
 * basis at a site right now, and `null` otherwise (never an invented number).
 */
export interface PersonListEntry {
  user_id: string;
  label: string;
  /** The WORK email this org knows them by (0166) — never a personal one. */
  email: string | null;
  /** The work name, else null. */
  display_name: string | null;
  /** The name the org's directory gives them, if any. */
  directory_name?: string | null;
  /** The work photo; null means no photo — never the personal one. */
  photo_url?: string | null;
  role: Role;
  deprovisioned_at: string | null;
  status: string;
  /**
   * True when this membership was created by SCIM (Entra) or a CSV import rather
   * than the person accepting an invitation themselves — the "provisioned"
   * origin (`memberships.provisioned`). Surfaced as a small tag on the People
   * list so an admin can tell an Entra/CSV-added person apart from a self-joined
   * one; it never changes what the person can do, only how they got here.
   */
  provisioned: boolean;
  teams: { id: string; name: string }[];
  sites: { id: string; name: string }[];
  warden: string[];
  onSite: boolean | null;
  reach: { app: boolean; sms: boolean; email: boolean };
  /**
   * Their work mobile (migration 0138) — present only for owners/admins
   * (`workMobilesVisible`); absent, not null, for a viewer, so no screen can
   * read "no number" as a fact it was never told.
   */
  workMobile?: WorkMobile | null;
}

export interface PeopleList {
  members: PersonListEntry[];
  /** Whether this reader sees colleagues' work mobiles (owner/admin only). */
  workMobilesVisible?: boolean;
  pendingInvitations: PendingInvitation[];
  counts: { active: number; admins: number; deprovisioned: number; pending: number };
}

/**
 * The People list, optionally narrowed by role, team and site. The filters
 * shape the returned `members`; `counts` stay the full-roster totals, because
 * "is the building covered" is a question about the whole roster.
 */
export const getPeople = (
  orgId: string,
  filters?: { role?: string; team?: string; site?: string },
) => {
  const qs = new URLSearchParams();
  if (filters?.role) qs.set('role', filters.role);
  if (filters?.team) qs.set('team', filters.team);
  if (filters?.site) qs.set('site', filters.site);
  const q = qs.toString();
  return api<PeopleList>(`/orgs/${orgId}/people${q ? `?${q}` : ''}`);
};

// ---- teams ------------------------------------------------------------------

/**
 * A Team — a named, org-scoped grouping that organises and filters people
 * (docs/teams.md). Never derived from a location; not an alert audience in v1.
 * `sites` is the buildings this team spans (many-to-many, and often none).
 */
export interface Team {
  id: string;
  name: string;
  createdAt: string;
  memberCount: number;
  sites: { id: string; name: string }[];
  /**
   * The team's designated manager, or null (migration 0091 — the team-scoped-
   * admin seed). Only the user id crosses the wire; the UI resolves the label
   * from the People list, so the teams route stays PII-free.
   */
  managerUserId: string | null;
  /**
   * Whether the signed-in person may write to THIS team: rename it, add and
   * remove its members, link and unlink its sites. True for owner/admin on
   * every team; true for the team's own manager only when their org role can
   * write (staff or above — a viewer who is designated manager reads false);
   * never derived client-side (docs/teams.md §"The team manager"). Creating
   * and deleting a team and choosing its manager stay admin-only regardless.
   */
  canManage: boolean;
}

export const getTeams = (orgId: string) => api<{ teams: Team[] }>(`/orgs/${orgId}/teams`);

/**
 * What an activation on this site would reach — answered by the SAME
 * predicates the send uses (`GET .../audience-preview`, admin read). `basis`
 * says which denominator: presence, roster, or team_roster (the roster
 * narrowed to the teams linked to the site + its wardens and admins —
 * migration 0113). The raise screen's "This will alert N people" and the
 * wizard's count both read THIS, never a client-side roster count, which lies
 * the moment the audience is presence- or team-scoped.
 */
export const audiencePreview = (
  orgId: string,
  siteId: string,
  mode: 'drill' | 'live' = 'drill',
  /** The scope the operator chose; the server reads `site_all` when none is sent. */
  scope?: AudienceScope,
) =>
  api<{
    count: number;
    /**
     * Approved followers the scope reaches who are not already in `count` —
     * beside the count, never inside it. Absent on an older API.
     */
    followers?: number;
    basis: 'presence' | 'roster' | 'team_roster';
    /** Migration 0134 — absent on an older API, read as one_person. */
    activationRule?: ActivationRule;
    /** Current administrators and owners — whether a second one exists. */
    adminCount?: number;
  }>(
    `/orgs/${orgId}/sites/${siteId}/audience-preview?mode=${mode}${scope ? `&scope=${scope}` : ''}`,
  );

export const createTeam = (orgId: string, name: string) =>
  api<{ id: string }>(`/orgs/${orgId}/teams`, { method: 'POST', body: JSON.stringify({ name }) });

export const renameTeam = (orgId: string, teamId: string, name: string) =>
  api<null>(`/orgs/${orgId}/teams/${teamId}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });

export const deleteTeam = (orgId: string, teamId: string) =>
  api<null>(`/orgs/${orgId}/teams/${teamId}`, { method: 'DELETE' });

export const addTeamMember = (orgId: string, teamId: string, userId: string) =>
  api<null>(`/orgs/${orgId}/teams/${teamId}/members`, {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });

export const removeTeamMember = (orgId: string, teamId: string, userId: string) =>
  api<null>(`/orgs/${orgId}/teams/${teamId}/members/${userId}`, { method: 'DELETE' });

export const addTeamSite = (orgId: string, teamId: string, siteId: string) =>
  api<null>(`/orgs/${orgId}/teams/${teamId}/sites`, {
    method: 'POST',
    body: JSON.stringify({ siteId }),
  });

export const removeTeamSite = (orgId: string, teamId: string, siteId: string) =>
  api<null>(`/orgs/${orgId}/teams/${teamId}/sites/${siteId}`, { method: 'DELETE' });

/**
 * Designate (or re-designate) a team's manager — the team-scoped-admin seed
 * (migration 0091). The person must already be a member of the team.
 */
export const setTeamManager = (orgId: string, teamId: string, userId: string) =>
  api<null>(`/orgs/${orgId}/teams/${teamId}/manager`, {
    method: 'PUT',
    body: JSON.stringify({ userId }),
  });

/** Remove a team's manager designation, leaving the team with none. */
export const clearTeamManager = (orgId: string, teamId: string) =>
  api<null>(`/orgs/${orgId}/teams/${teamId}/manager`, { method: 'DELETE' });

/**
 * Invite one person by address (`POST /orgs/:orgId/people`, the setup-wizard-api
 * contract). Creates an invitation the person can decline in one tap without
 * creating an account. `already_a_member` comes back when the address is already
 * on the roster, so the caller can say so rather than silently doing nothing.
 */
export const invitePerson = (
  orgId: string,
  email: string,
  role: 'admin' | 'viewer' | 'staff' | 'occupant',
  /** The team they will join on accepting — optional, applied at accept. */
  teamId?: string,
) =>
  api<{ invitationId?: string; status?: string }>(`/orgs/${orgId}/people`, {
    method: 'POST',
    body: JSON.stringify({ email, role, ...(teamId ? { teamId } : {}) }),
  });

/**
 * Resend a pending invitation (`POST /orgs/:orgId/people/:invitationId/resend`).
 * Rotates the token and extends its expiry, and is rate-limited server-side —
 * a refusal comes back as an ApiError the caller shows verbatim.
 */
export const resendInvitation = (orgId: string, invitationId: string) =>
  api<{ status?: string }>(`/orgs/${orgId}/people/${invitationId}/resend`, {
    method: 'POST',
  });

/**
 * Remove a person or a pending invitation by id (`DELETE /orgs/:orgId/people/:id`).
 * `id` is the member's `user_id` for someone on the roster, or the invitation's
 * id for someone who has not accepted. The server refuses to remove the last
 * owner/admin (the at-most-one-owner / last-admin trigger); that refusal comes
 * back as an ApiError with a friendly message to show.
 *
 * The body carries `vacatedEcoRoles` when removing this person also emptied one
 * or more emergency-control roles they held, so the caller can name the gap on
 * the spot rather than leave it to be found on a readiness screen a fortnight
 * later.
 */
export const removePerson = (orgId: string, id: string) =>
  api<{ removed: boolean; vacatedEcoRoles?: string[] }>(`/orgs/${orgId}/people/${id}`, {
    method: 'DELETE',
  });

/**
 * Hand the Owner seat to a different admin. Owner-only, one-way, atomic on
 * the server (DELETE old + INSERT new in one transaction).
 */
/**
 * Getting the organisation confirmed (migration 0150). Until it is, a live
 * alert reaches the people who joined — accepted members, followers, visitors
 * who signed themselves in — but not a roster row nobody accepted. A Duress
 * operator decides; the org's side is to ask, and to see where the ask stands.
 */
export type OrgVerificationStatus = 'unconfirmed' | 'pending' | 'confirmed' | 'rejected';

export interface OrgVerification {
  status: OrgVerificationStatus;
  confirmedAt: string | null;
  canSubmit: boolean;
  rejection: { reason: string | null; decidedAt: string | null } | null;
  request: {
    submittedAt: string;
    legalName: string;
    countryCode: string;
    businessNumberKind: string;
    businessNumber: string;
    website: string;
    role: string;
  } | null;
}

export type BusinessNumberKind = 'abn' | 'acn' | 'nzbn' | 'uk_company' | 'ein' | 'other';

export interface OrgVerificationSubmission {
  legalName: string;
  countryCode: string;
  businessNumberKind: BusinessNumberKind;
  businessNumber: string;
  website: string;
  role: string;
}

export const getOrgVerification = (orgId: string) =>
  api<OrgVerification>(`/orgs/${orgId}/verification`);

export const submitOrgVerification = (orgId: string, body: OrgVerificationSubmission) =>
  api<{ requestId: string; status: 'pending' }>(`/orgs/${orgId}/verification`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const transferOwnership = (orgId: string, toUserId: string) =>
  api<{ ok?: boolean }>(`/orgs/${orgId}/transfer-ownership`, {
    method: 'POST',
    body: JSON.stringify({ toUserId }),
  });

/**
 * A whole roster, as the text of a CSV.
 *
 * Two calls on purpose. The preview returns a plan and a **digest**; the
 * import must present that digest back, so a file edited between looking and
 * applying is refused rather than quietly removing somebody the admin never
 * saw on a list.
 */
export type RosterRole = 'admin' | 'staff' | 'occupant';

export interface RosterPlan {
  add: { email: string; name: string | null; role: RosterRole }[];
  changeRole: { email: string; from: RosterRole; to: RosterRole }[];
  remove: { email: string; role: RosterRole }[];
  unchanged: number;
  digest: string;
  /**
   * The number of active people on the roster now — the denominator the server
   * measures the >50% guard against. The dashboard uses this exact figure in
   * the warning sentence rather than recomputing a count from the plan, so the
   * threshold shown matches the one enforced (Arch M3). Optional only while
   * this client runs ahead of the setup-wizard-api merge; absent, the client
   * falls back to summing the plan's active buckets.
   */
  activeCount?: number;
  /**
   * True when the plan would take off more than half of the current active
   * roster (`api/src/roster/reconcile.ts`, the >50% guard). `applyRoster`
   * refuses such a plan unless `confirmLargeRemoval: true` is passed, so the
   * dashboard reads this to show the confirmation dialog before ever asking
   * the server. Absent (older server) means "not flagged"; the client also
   * computes it defensively from the plan so the guard cannot be bypassed by
   * a missing field.
   */
  largeRemoval?: boolean;
}

export const previewRoster = (orgId: string, csv: string) =>
  api<RosterPlan>(`/orgs/${orgId}/roster/preview`, {
    method: 'POST',
    body: JSON.stringify({ csv }),
  });

/**
 * Apply a reconciled roster. `confirmLargeRemoval` acknowledges a plan that
 * removes more than half the active roster — without it the server refuses such
 * a plan (the >50% guard), so a file that would silently gut a building is
 * stopped until an admin has seen the removals and said yes.
 */
export const importRoster = (
  orgId: string,
  csv: string,
  digest: string,
  confirmLargeRemoval = false,
) =>
  api<{ added: number; changed: number; removed: number }>(`/orgs/${orgId}/roster/import`, {
    method: 'POST',
    body: JSON.stringify({ csv, digest, confirmLargeRemoval }),
  });

/**
 * Guardian follows — docs/roster-and-presence.md §2.
 *
 * The organisation authorises a guardian→person relationship; we consume it.
 * There is **no self-service path**, so these calls are admin-only by design —
 * the dashboard is the school authorising. A guardian sees status and the
 * reunification instruction only, **never location**, and revocation
 * (`revokeGuardianFollow`) is immediate and silent: nobody is notified, because
 * telling a removed guardian is a signal to someone dangerous.
 */
export interface GuardianFollow {
  id: string;
  site_id: string;
  site_name: string;
  guardian_user_id: string;
  guardian_name: string | null;
  guardian_email: string | null;
  person_user_id: string;
  person_name: string | null;
  authorised_by_user_id: string;
  authorised_at: string;
}

export const getGuardianFollows = (orgId: string) =>
  api<{ follows: GuardianFollow[] }>(`/orgs/${orgId}/guardian-follows`);

export const authoriseGuardianFollow = (
  orgId: string,
  body: { guardianUserId: string; personUserId: string; siteId: string },
) =>
  api<{ followId: string }>(`/orgs/${orgId}/guardian-follows`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const revokeGuardianFollow = (orgId: string, followId: string) =>
  api<null>(`/orgs/${orgId}/guardian-follows/${followId}`, { method: 'DELETE' });

/* ---------------------------------------------------------------------- ECO */

export interface EcoAssignment {
  id: string;
  role: string;
  succession_order: number;
  zone_name: string | null;
  display_name: string | null;
  training_overdue: boolean;
  trained_at: string | null;
}

export interface EcoBoard {
  assignments: EcoAssignment[];
  readiness: { vacantCriticalRoles: string[]; trainingOverdue: number; untrained: number };
}

export const getEco = (orgId: string, siteId: string) =>
  api<EcoBoard>(`/orgs/${orgId}/sites/${siteId}/eco`);

export const assignEco = (
  orgId: string,
  siteId: string,
  body: { userId: string; role: string; zoneId?: string | null; successionOrder?: number },
) =>
  api<{ assignmentId: string }>(`/orgs/${orgId}/sites/${siteId}/eco`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const vacateEco = (orgId: string, siteId: string, assignmentId: string) =>
  api<null>(`/orgs/${orgId}/sites/${siteId}/eco/${assignmentId}/vacate`, { method: 'POST' });

/* ----------------------------------------------------------------- presence */

export interface PresenceRow {
  id: string;
  user_id: string;
  display_name: string | null;
  kind: 'member' | 'visitor' | 'contractor';
  signed_in_at: string;
  expected_departure_at: string;
  host: string | null;
  purpose: string | null;
  abandoned: boolean;
}

export interface PresenceBoard {
  present: PresenceRow[];
  counts: { total: number; members: number; visitors: number; abandoned: number };
  basis: 'presence' | 'roster';
}

export const getPresence = (orgId: string, siteId: string) =>
  api<PresenceBoard>(`/orgs/${orgId}/sites/${siteId}/presence`);

export const signIn = (
  orgId: string,
  siteId: string,
  body: {
    userId?: string;
    name?: string;
    kind?: string;
    expectedMinutes?: number;
    purpose?: string;
  },
) =>
  api<{ presenceId: string; userId: string; expectedDepartureAt: string }>(
    `/orgs/${orgId}/sites/${siteId}/presence`,
    { method: 'POST', body: JSON.stringify(body) },
  );

export const signOutPresence = (orgId: string, siteId: string, presenceId: string) =>
  api<null>(`/orgs/${orgId}/sites/${siteId}/presence/${presenceId}/out`, {
    method: 'POST',
    body: JSON.stringify({ method: 'manual' }),
  });

/* ------------------------------------------------------------------- events */

export interface SweepBoard {
  zones: {
    zoneId: string;
    zoneName: string;
    state: string;
    headcount: number | null;
    reportedBy: string | null;
    warden: string | null;
    secondsSinceReport: number | null;
    latitude: number | null;
    longitude: number | null;
  }[];
  swept: number;
  total: number;
  allAreasClear: boolean;
  cannotAccess: number;
}

export interface LiveReadiness {
  live: boolean;
  liveEnabledAt: string | null;
  qualifiedScope: string | null;
  /**
   * Whether this org qualified via the single-admin preview-and-retract path
   * (migration 0064, `docs/security.md` §1.1 tier 2). Named on the response
   * so the dashboard can render the mechanism the operator is actually
   * relying on rather than leaving them to infer it from admin count.
   */
  singleAdminRetract: boolean;
  /**
   * The one-tap retract deadline used on this path, in seconds. Bounded
   * 30–180 by CHECK on `orgs.retract_window_seconds`; defaults to the
   * system value when the org has no override.
   */
  retractWindowSeconds: number;
  conditions: {
    allClearTemplates: { ok: boolean; eventTypes: string[] };
    /**
     * A pre-drafted correction template (R3), reported as its own gap
     * rather than folded under a generic "templates missing" header —
     * R3 is the primary control on the preview-and-retract path, so a
     * single-admin org shopping around tier 2 needs to see which template
     * they still owe rather than discover it during qualification.
     */
    correctionTemplates: {
      ok: boolean;
      eventTypes: string[];
      requiredForPreviewAndRetract: boolean;
    };
    drillCompleted: { ok: boolean; lastClosedAt: string | null };
    twoAdmins: {
      ok: boolean;
      adminCount: number;
      requiredForScope: string;
      /** True when preview-and-retract offers a single-admin path around this gap. */
      previewAndRetractAvailable: boolean;
    };
    authorityAttested: {
      ok: boolean;
      ecoAuthority: string | null;
      requestedBy: string | null;
      attestedBy: string | null;
      requiredForScope: string;
    };
  };
}

export const getLiveReadiness = (orgId: string) =>
  api<LiveReadiness>(`/orgs/${orgId}/live/readiness`);

export const requestLive = (orgId: string, ecoAuthority: string) =>
  api<{ status: string }>(`/orgs/${orgId}/live/request`, {
    method: 'POST',
    body: JSON.stringify({ ecoAuthority }),
  });

export const attestLive = (orgId: string) =>
  api<{ status: string }>(`/orgs/${orgId}/live/attest`, { method: 'POST' });

export const getSweeps = (orgId: string, eventId: string) =>
  api<SweepBoard>(`/orgs/${orgId}/events/${eventId}/sweeps`);

export const correctEvent = (orgId: string, eventId: string) =>
  api<null>(`/orgs/${orgId}/events/${eventId}/correct`, { method: 'POST' });

/**
 * The one-tap retract on the preview-and-retract path (migration 0064,
 * `docs/security.md` §1.1 tier 2 and §R3).
 *
 * Semantically the same operation as `correctEvent` — state moves to
 * `corrected`, CAP `msgType = Cancel` is set by trigger, the same correction
 * template fanout runs — with `retracted_within_window` recorded on the row
 * so signed reports and audit logs can tell the R3 primary control's use
 * apart from an ordinary post-window correction.
 *
 * **Retracting must never be harder than sending** — one admin, no freshness
 * gate, no confirmation step. A repeat POST inside the window is idempotent
 * on `corrected`, because a panicking admin can hit the button more than
 * once.
 */
export const retractEvent = (orgId: string, eventId: string) =>
  api<{ retracted?: true; alreadyRetracted?: true; withinWindow: boolean }>(
    `/orgs/${orgId}/events/${eventId}/retract`,
    { method: 'POST' },
  );

export interface TransitionBody {
  procedure: 'evacuation' | 'invacuation' | 'lockdown' | 'communication';
  instruction: string;
  /** Omitted resolves to the new procedure's default on the server. */
  silent?: boolean;
}

/**
 * Change the procedure of a live event mid-incident (migration 0076,
 * `docs/receiving-experience-map.md` §Transitions) — an evacuation that
 * becomes a lockdown when a weapon appears in the car park, or the reverse
 * when a fire starts during a lockdown.
 *
 * A transition is a *new alert* under the new procedure, delivered to the
 * same audience; it is recorded append-only, the current procedure becomes the
 * latest transition, and everyone alerted must answer again. The server
 * enforces the safety rules — a lockdown transition may never be sent loud,
 * an evacuation may never be silent — and refuses a transition on anything but
 * a live event.
 */
export const transitionEvent = (orgId: string, eventId: string, body: TransitionBody) =>
  api<{ transitionId: string; seq: number; from: string; to: string; silent: boolean }>(
    `/orgs/${orgId}/events/${eventId}/transition`,
    { method: 'POST', body: JSON.stringify(body) },
  );

export interface CommandBoard {
  roles: {
    role: string;
    holder: string | null;
    source: 'handover' | 'standing' | 'vacant';
    inSuccession: boolean | null;
  }[];
  handovers: {
    role: string;
    assumed_at: string;
    to_name: string | null;
    from_name: string | null;
  }[];
}

export const getCommand = (orgId: string, eventId: string) =>
  api<CommandBoard>(`/orgs/${orgId}/events/${eventId}/command`);

export const recordHandover = (
  orgId: string,
  eventId: string,
  body: { userId: string; role: string; reason?: string },
) =>
  api<{ ok: boolean }>(`/orgs/${orgId}/events/${eventId}/command`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const fileSweep = (
  orgId: string,
  eventId: string,
  body: { zoneId: string; state: string; headcount?: number; note?: string },
) =>
  api<{ ok: boolean }>(`/orgs/${orgId}/events/${eventId}/sweeps`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/* ------------------------------------------------------------------ reports */

export interface StoredReport {
  reportId: string;
  generatedAt: string;
  generatedBy: string | null;
  contentHash: string;
  shortHash: string;
  signature: string | null;
  signingKeyId: string | null;
  content: Record<string, unknown>;
}

/**
 * Fetch the rendered report HTML with the caller's bearer token.
 *
 * The API's `/report.html` route is auth-gated (an operator's compliance
 * document is not something anyone can request by URL), and a naive
 * `<a href="…">` opened in a new tab sends no headers and 401s — which is
 * what the "Open printable version" link did on staging on 2026-09-01.
 * Fetch the HTML with auth, hand the browser a Blob URL, and print/save
 * runs off the same self-contained document. The auth model does not change.
 */
export async function fetchReportHtml(orgId: string, eventId: string): Promise<string> {
  const doFetch = async (retried: boolean): Promise<Response> => {
    const access = session.access;
    const res = await fetch(
      `${BASE}/orgs/${orgId}/events/${eventId}/report.html`,
      access ? { headers: { authorization: `Bearer ${access}` } } : {},
    );
    if (res.status === 401 && !retried && session.refresh) {
      if (await refreshSession()) return doFetch(true);
    }
    return res;
  };
  const res = await doFetch(false);
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return res.text();
}

export const getReport = (orgId: string, eventId: string) =>
  api<StoredReport>(`/orgs/${orgId}/events/${eventId}/report`);

export interface Verification {
  contentHash: string;
  shortHash: string;
  hashMatches: boolean | null;
  signatureValid: boolean | null;
  verdict: string;
}

/** Unauthenticated on purpose — the same check a regulator would run. */
export const verifyReport = (body: {
  content: Record<string, unknown>;
  signature?: string | null;
  signingKeyId?: string | null;
  expectedContentHash?: string;
}) => api<Verification>('/reports/verify', { method: 'POST', body: JSON.stringify(body) });

export const generateReport = (orgId: string, eventId: string) =>
  api<StoredReport & { unsignedBecause: { reason: string } | null }>(
    `/orgs/${orgId}/events/${eventId}/report`,
    { method: 'POST' },
  );

/* ----------------------------------------------------------------- channels */

export interface Channel {
  id: string;
  kind: 'slack_webhook' | 'teams_webhook';
  label: string | null;
  site_id: string | null;
  site_name: string | null;
  drills_included: boolean;
  active: boolean;
  created_at: string;
}

export const getChannels = (orgId: string) =>
  api<{ channels: Channel[] }>(`/orgs/${orgId}/channels`);

export const addChannel = (
  orgId: string,
  body: { kind: Channel['kind']; endpoint: string; label?: string; drillsIncluded?: boolean },
) =>
  api<{ channelId: string }>(`/orgs/${orgId}/channels`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateChannel = (
  orgId: string,
  channelId: string,
  body: { label?: string | null; drillsIncluded?: boolean },
) =>
  api<{ ok: boolean }>(`/orgs/${orgId}/channels/${channelId}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const removeChannel = (orgId: string, channelId: string) =>
  api<{ ok: boolean }>(`/orgs/${orgId}/channels/${channelId}/remove`, { method: 'POST' });

/**
 * Send a harmless test message to a connected channel. The server opens the
 * sealed webhook, posts and discards it — the URL never comes back — and a
 * webhook that does not respond surfaces as an ApiError (502) the caller shows.
 */
export const testChannel = (orgId: string, channelId: string) =>
  api<{ ok: boolean }>(`/orgs/${orgId}/channels/${channelId}/test`, { method: 'POST' });

/* --------------------------------------------------------------------- org */

export interface OrgDetail {
  id: string;
  name: string;
  alert_locales: string[];
}

export const getOrg = (orgId: string) => api<OrgDetail>(`/orgs/${orgId}`);

export const updateOrg = (orgId: string, body: { name?: string; alertLocales?: string[] }) =>
  api<{ ok: boolean; warnings: string[] }>(`/orgs/${orgId}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const translateTemplates = (orgId: string) =>
  api<{
    translated: { capEvent: string; kind: string; language: string }[];
    failed: { capEvent: string; kind: string; language: string; error: string }[];
  }>(`/orgs/${orgId}/templates/translate`, { method: 'POST', body: JSON.stringify({}) });

/* ------------------------------------------------------------------- venues */

export interface NearbyVenue {
  id: string;
  name: string;
  address: string | null;
  locality: string | null;
  claimed: boolean;
  waiting: number;
  distance_km: number;
}

export const venuesNearby = (latitude: number, longitude: number) =>
  api<{ venues: NearbyVenue[] }>(
    `/venues/nearby?latitude=${latitude}&longitude=${longitude}&radiusKm=5`,
  );

export interface Tenancy {
  id: string;
  venue_id: string;
  venue_name: string;
  site_id: string;
  site_name: string | null;
  occupier_name: string | null;
  state: 'pending' | 'confirmed';
  created_at: string;
  mine: boolean;
}

export const getTenancies = (orgId: string) =>
  api<{ tenancies: Tenancy[] }>(`/orgs/${orgId}/tenancies`);

export const requestTenancy = (orgId: string, venueId: string, siteId: string) =>
  api<{ tenancyId?: string; status?: string }>(`/orgs/${orgId}/venues/${venueId}/tenancy`, {
    method: 'POST',
    body: JSON.stringify({ siteId }),
  });

export const confirmTenancy = (orgId: string, tenancyId: string) =>
  api<{ ok: boolean }>(`/orgs/${orgId}/tenancies/${tenancyId}/confirm`, { method: 'POST' });

/**
 * Escalate a running event to the venue this site sits inside.
 *
 * The Kmart-at-Westfield case (`docs/roster-and-presence.md`): a tenant
 * admin can page the building manager when something local needs the
 * centre to know. The server accepts this on
 * `POST /orgs/:orgId/events/:eventId/escalate` (see
 * `api/src/routes/escalations.ts:318`) and refuses without a confirmed
 * `venue_tenancies` handshake — the relationship comes first. Fanout is
 * an ack-pageback loop: the worker walks the operator's `venue_oncall`
 * roster every ESCALATION_REPAGE_SECONDS until a human at the centre
 * acknowledges.
 *
 * The tenant sees ack when it lands via the standard event view.
 */
export const escalateEventToVenue = (
  orgId: string,
  eventId: string,
  venueId: string,
  body: string,
) =>
  api<{ escalationId: string }>(`/orgs/${orgId}/events/${eventId}/escalate`, {
    method: 'POST',
    body: JSON.stringify({ venueId, body }),
  });

export const operatedVenues = (orgId: string) =>
  api<{
    venues: {
      id: string;
      name: string;
      locality: string | null;
      logo_url: string | null;
    }[];
  }>(`/orgs/${orgId}/operated-venues`);

/**
 * Upload a venue logo. The endpoint stamps `venues.logo_url` and
 * returns the URL — precedence-wise this beats the Wikidata default,
 * because a rebrand (Officeworks 2023, Ampol 2020) leaves Wikimedia
 * lagging by months and the org knows what its current wordmark is.
 *
 * `fetch` directly rather than through `api()` because the shared
 * helper serialises the body as JSON and sets `content-type`
 * accordingly — a multipart upload needs neither. The auth header
 * is mirrored by hand so both paths carry the same identity.
 */
export async function uploadVenueLogo(
  orgId: string,
  venueId: string,
  file: File,
): Promise<{ logoUrl: string }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${BASE}/orgs/${orgId}/venues/${venueId}/logo`, {
    method: 'POST',
    headers: { authorization: `Bearer ${session.access ?? ''}` },
    body: fd,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? `HTTP ${res.status}`, body);
  }
  return (await res.json()) as { logoUrl: string };
}

export const clearVenueLogo = (orgId: string, venueId: string) =>
  api<void>(`/orgs/${orgId}/venues/${venueId}/logo`, { method: 'DELETE' });

export interface OncallEntry {
  id: string;
  position: number;
  user_id: string;
  name: string;
}

export const getOncall = (orgId: string, venueId: string) =>
  api<{ oncall: OncallEntry[] }>(`/orgs/${orgId}/venues/${venueId}/oncall`);

export const addOncall = (orgId: string, venueId: string, userId: string, position: number) =>
  api<{ oncallId: string }>(`/orgs/${orgId}/venues/${venueId}/oncall`, {
    method: 'POST',
    body: JSON.stringify({ userId, position }),
  });

export const removeOncall = (orgId: string, venueId: string, oncallId: string) =>
  api<{ ok: boolean }>(`/orgs/${orgId}/venues/${venueId}/oncall/${oncallId}/remove`, {
    method: 'POST',
  });

/**
 * Every QR a building prints (docs/qr-codes.md). `follow` is null for a
 * sensitive building, with `followRefusal` saying why in words.
 */
export interface QrLinks {
  siteName: string;
  follow: { url: string } | null;
  followRefusal: string | null;
  kiosk: { url: string };
}

export const getQrLinks = (orgId: string, siteId: string) =>
  api<QrLinks>(`/orgs/${orgId}/sites/${siteId}/qr-links`);

/* -------------------------------------------------------------- escalations */

export interface Escalation {
  id: string;
  venue_id: string;
  venue_name: string;
  event_id: string;
  site_name: string | null;
  occupier_name: string | null;
  body: string;
  created_at: string;
  last_notified_at: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  direction: 'inbound' | 'outbound';
}

export const getEscalations = (orgId: string) =>
  api<{ escalations: Escalation[] }>(`/orgs/${orgId}/escalations`);

/**
 * Occupier → operator: tell the centre something is happening on the site
 * they operate. The API only accepts it if a `venue_tenancies` handshake
 * is confirmed between this org's site and the venue; the button on Live
 * is hidden otherwise. Ack is a separate act by the operator.
 */
export const escalateEvent = (orgId: string, eventId: string, venueId: string, body: string) =>
  api<{ escalationId: string }>(`/orgs/${orgId}/events/${eventId}/escalate`, {
    method: 'POST',
    body: JSON.stringify({ venueId, body }),
  });

/**
 * Mint the bearer token an admin pastes into Entra (or any SCIM 2.0 client)
 * so the customer's roster provisions and de-provisions itself. Pro-tier
 * only; the server returns 402 with detail describing the entitlement gap
 * for a free-tier org, and the UI reads that into an upgrade prompt.
 * The token is *shown once* — the response is the only place it will ever
 * appear, so the client copies it to the clipboard and warns the admin.
 */
export const mintScimToken = (orgId: string) =>
  api<{ token: string; endpoint: string }>(`/orgs/${orgId}/scim-token`, {
    method: 'POST',
  });

export const ackEscalation = (orgId: string, escalationId: string) =>
  api<{ ok: boolean }>(`/orgs/${orgId}/escalations/${escalationId}/ack`, { method: 'POST' });

/* ------------------------------------------------------------------ billing */

export interface Entitlements {
  plan: 'free' | 'pro' | 'platform';
  state: 'active' | 'past_due' | 'cancelled';
  siteAllowance: number;
  sitesUsed: number;
  /** The workforce meter. Observational — it never blocks anything. */
  seatsUsed: number;
  seatCommitment: number | null;
  /**
   * Whether the evidence module is turned on for this org (a provisioned
   * entitlement, not a billing plan). Gates whether Settings offers the
   * evidence workspace — the DOJ NSW pilot has it, an ordinary org does not.
   */
  evidence: boolean;
}

export const getEntitlements = (orgId: string) => api<Entitlements>(`/orgs/${orgId}/entitlements`);

export const startCheckout = (orgId: string, quantity: number) =>
  api<{ url: string }>(`/orgs/${orgId}/billing/checkout`, {
    method: 'POST',
    body: JSON.stringify({ quantity }),
  });

export const billingPortal = (orgId: string) =>
  api<{ url: string }>(`/orgs/${orgId}/billing/portal`, { method: 'POST' });

export async function signOut(): Promise<void> {
  const token = session.refresh;
  session.clear();
  if (token) {
    await fetch(`${BASE}/auth/logout`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken: token }),
    }).catch(() => {
      // The local session is already gone; a failed revoke is worth neither a
      // retry nor an error in the operator's face.
    });
  }
}

/* ------------------------------------------------------- the Direct record */

/**
 * The ground as it stands in THIS event — the site says the oval is an
 * assembly point; the event says the oval is shut because that is where the
 * smoke went (migration 0035). Current state is the latest update per subject.
 */
export interface GroundPoint {
  id: string;
  name: string;
  /** Which procedure sends people here (migration 0030). Absent on an older API. */
  kind?: AssemblyKind;
  latitude: number;
  longitude: number;
  open: boolean;
  step_free_access: boolean | null;
  /** Nothing records which point a confirmation happened at yet; zero is the honest answer. */
  confirmed_here: number;
  closed_reason: string | null;
}

export interface GroundZone {
  id: string;
  name: string;
  /** Hazard is a second axis, not a sweep state: a zone swept clear at 10:31 can be the most dangerous place at 10:34. */
  hazard: boolean;
  hazard_note: string | null;
  swept: boolean;
}

export const getGround = (orgId: string, eventId: string) =>
  api<{ points: GroundPoint[]; zones: GroundZone[] }>(`/orgs/${orgId}/events/${eventId}/ground`);

export type UpdateKind =
  'instruction' | 'point_closed' | 'point_opened' | 'hazard_marked' | 'hazard_cleared' | 'note';

/** One row of the append-only update log, newest first. Delivered and undelivered both. */
export interface EventUpdate {
  id: string;
  kind: UpdateKind;
  body: string;
  subject_id: string | null;
  subject_name: string | null;
  at: string;
  by_name: string | null;
  delivered: boolean;
  recipients: number | null;
}

export const getUpdates = (orgId: string, eventId: string) =>
  api<{ updates: EventUpdate[] }>(`/orgs/${orgId}/events/${eventId}/updates`);

/**
 * Change the plan mid-event. A change to the ground is a message, or it is
 * worse than nothing: the server refuses a closure with no open redirect and
 * refuses to close the last open point, in words the operator can act on.
 */
export const postUpdate = (
  orgId: string,
  eventId: string,
  body: { kind: UpdateKind; body: string; subject_id?: string; redirect_to_id?: string },
) =>
  api<{ updateId: string }>(`/orgs/${orgId}/events/${eventId}/updates`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export type ReportKind = 'hazard' | 'needs_help' | 'blocked_route' | 'other';

/**
 * What people on the ground are reporting in. A reading, never a message —
 * there is deliberately no forward-this-report call.
 */
export interface InboundReport {
  id: string;
  kind: ReportKind;
  body: string;
  at: string;
  source: string;
  state: 'new' | 'acted' | 'dismissed';
  zone_name: string | null;
  place_said: string | null;
  has_photo: boolean;
  /** Independent OTHER reporters saying the same about the same place — never volume. */
  corroborations: number;
}

export const getReports = (orgId: string, eventId: string) =>
  api<{ reports: InboundReport[] }>(`/orgs/${orgId}/events/${eventId}/reports`);

/** The operator's decision, recorded once and never flipped. */
export const decideReport = (
  orgId: string,
  eventId: string,
  reportId: string,
  decision: 'acted' | 'dismiss',
) =>
  api<unknown>(`/orgs/${orgId}/events/${eventId}/reports/${reportId}/${decision}`, {
    method: 'POST',
  });
